import BaseSetup from '@/components/BaseSetup';
import SelfEntity from '@/entity/SelfEntity';
import { useCompanyAgencyService } from '@/services/company/CompanyAgencyService';
import { useCompanyClientService } from '@/services/company/CompanyClientService';
import { useCompanyService } from '@/services/company/CompanyService';
import { Reactive } from '@/utils/decorators';
import { watch } from 'vue';

export type SharedSelectCompanyEmits = {
	(e: 'update:modelValue' | 'input', id: Undef<number | number[]>): void
	(e: 'select', companyModel: ICompanyModel | [ICompanyModel]): void
	(e: 'loaded'): void
};

type CompanyElement = ICompanyWithCheckPluginOrderModel & {
	disabled: boolean
};

export type SharedSelectCompanyProps = {
	checkOrderPlugin?: boolean
	hideAgencyName?: boolean
	setToFirst?: boolean
	clearable?: boolean
	modelValue?: number
	permissionAction?: PERMISSION_ACTION
	type?: InstanceType<typeof SelfEntity>['readableUserType']
	agencyCompanyId?: number
};

export default class SharedSelectCompanySetup extends BaseSetup {

	static readonly defaultProps = { clearable: true };

	@Reactive private readonly state = { companies: [] as Partial<CompanyElement>[] };

	get companies () { return this.state.companies }
	get searchable () { return this.companies && this.companies.length >= 10 }
	get label () { return this.props.type === 'client' ? this.$t('common.label.company') : this.$t('common.label.agency') }
	get publicInstance () {
		return { getOptions: () => this.companies as ICompanyWithCheckPluginOrderModel[] };
	}

	get vModel () { return this.props.modelValue }
	set vModel (val) { this.emits('update:modelValue', val) }

	constructor (
		private props: SharedSelectCompanyProps,
		private emits: SharedSelectCompanyEmits,
	) {
		super();

		watch(() => this.props.agencyCompanyId, (val) => {
			if (val) this.vModel = undefined;
			this.onBeforeMountAsync();
		});
	}

	async onBeforeMountAsync () {
		if (this.props.type === 'client' && this.self.isAgency()) {
			const data = await useCompanyAgencyService().clientListAsync({
				checkOrderPlugin: false,
				agencyCompanyId: this.props.agencyCompanyId,
			});
			this.state.companies = data;
		}
		else if (this.props.type === 'agency' && this.self.isClient()) {
			const data = await useCompanyClientService().getAgencyListAsync(
				this.props.permissionAction,
				this.props.checkOrderPlugin,
			);
			this.state.companies = data;
		}
		else {
			const data = await useCompanyService().getAsync({
				permissionAction: this.props.permissionAction,
				checkOrderPlugin: this.props.checkOrderPlugin ?? false,
			});
			this.state.companies = data;
		}

		if (this.props.setToFirst) {
			const first = this.companies?.first();
			if (first?.id) {
				this.emits('update:modelValue', first.id);
				this.emits('input', first.id);
			}
		}

		this.emits('loaded');
	}

}
